import { useState, useEffect, useCallback } from "react";
import { adminApi, type AnalyticsSummary } from "@/lib/superadmin";
import { PLAN_LABEL } from "@/lib/access";
import {
  PageHeader,
  SectionCard,
  ErrorBanner,
  PageSkeleton,
  EmptyState,
  IconButton,
  AreaChart,
  BarChart,
  DonutChart,
} from "../components/ui";

function inr(value: number): string {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value.toLocaleString("en-IN")}`;
}

const RANK_TONES = [
  "bg-amber-100 text-amber-700",
  "bg-slate-200 text-slate-600",
  "bg-orange-100 text-orange-700",
];

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await adminApi.analytics.summary());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const topMax = Math.max(1, ...(data?.topTenants.map((t) => t.revenue) ?? []));

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Platform Analytics"
        subtitle="Revenue trend, plan distribution, top tenants, and signup trend"
        actions={
          <IconButton
            icon="ri-refresh-line"
            title="Refresh"
            spinning={loading}
            onClick={() => void load()}
          />
        }
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <SectionCard title="Revenue Trend" icon="ri-funds-line">
            {data?.revenueTrend.length ? (
              <AreaChart
                data={data.revenueTrend.map((r) => r.revenue)}
                labels={data.revenueTrend.map((r) => r.month)}
                format={inr}
                height={200}
              />
            ) : (
              <EmptyState icon="ri-line-chart-line" title="No revenue data yet" />
            )}
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Plan Distribution" icon="ri-pie-chart-2-line">
              {data?.planDistribution.length ? (
                <DonutChart
                  segments={data.planDistribution.map((p) => ({
                    label: PLAN_LABEL[p.plan] ?? p.plan,
                    value: p.count,
                  }))}
                  centerValue={String(
                    data.planDistribution.reduce((s, p) => s + p.count, 0),
                  )}
                  centerLabel="tenants"
                />
              ) : (
                <EmptyState icon="ri-pie-chart-2-line" title="No plan data yet" />
              )}
            </SectionCard>

            <SectionCard title="Signup Trend" icon="ri-user-add-line">
              {data?.signupTrend.length ? (
                <BarChart
                  data={data.signupTrend.map((s) => s.count)}
                  labels={data.signupTrend.map((s) => s.month)}
                  colorClass="bg-emerald-400 hover:bg-emerald-500"
                />
              ) : (
                <EmptyState icon="ri-user-add-line" title="No signup data yet" />
              )}
            </SectionCard>
          </div>

          <SectionCard title="Top Tenants by Revenue" icon="ri-trophy-line">
            {data?.topTenants.length ? (
              <div className="space-y-3.5">
                {data.topTenants.map((t, i) => (
                  <div key={t.restaurantId} className="flex items-center gap-3">
                    <span
                      className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold shrink-0 ${
                        RANK_TONES[i] ?? "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-foreground-700 font-medium truncate">
                          {t.name}
                        </span>
                        <span className="text-foreground-900 font-semibold ml-2 whitespace-nowrap">
                          {inr(t.revenue)}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all duration-500"
                          style={{ width: `${(t.revenue / topMax) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon="ri-trophy-line" title="No tenant revenue data yet" />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
